import React, { useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { getContentBankHighlightRanges } from './contentBankUtils';
import { createContentBankExtensions } from './tiptapExtensions';
import type { TiptapJsonNode } from './types';
import './contentBank.css';

type ContentBankRendererProps = {
  value: TiptapJsonNode;
  contentKey: string;
  searchQuery?: string;
};

const HIGHLIGHT_CLASS_NAME = 'content-bank-search-highlight';

function clearHighlights(root: HTMLElement) {
  root.querySelectorAll(`mark.${HIGHLIGHT_CLASS_NAME}`).forEach((highlight) => {
    const parent = highlight.parentNode;
    if (!parent) {
      return;
    }

    parent.replaceChild(document.createTextNode(highlight.textContent || ''), highlight);
    parent.normalize();
  });
}

function shouldSkipTextNode(node: Node) {
  const parent = node.parentElement;
  return Boolean(
    !node.textContent?.trim() ||
      parent?.closest(`mark.${HIGHLIGHT_CLASS_NAME}, script, style, iframe, video, audio`)
  );
}

function applyHighlights(root: HTMLElement, query: string) {
  clearHighlights(root);

  if (!query.trim()) {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return shouldSkipTextNode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes: Text[] = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    const ranges = getContentBankHighlightRanges(text, query);
    if (ranges.length === 0 || !node.parentNode) {
      return;
    }

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    ranges.forEach((range) => {
      if (range.start > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, range.start)));
      }

      const mark = document.createElement('mark');
      mark.className = HIGHLIGHT_CLASS_NAME;
      mark.textContent = text.slice(range.start, range.end);
      fragment.appendChild(mark);
      cursor = range.end;
    });

    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)));
    }

    node.parentNode.replaceChild(fragment, node);
  });
}

export default function ContentBankRenderer({
  value,
  contentKey,
  searchQuery = '',
}: ContentBankRendererProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const latestValueRef = useRef(value);
  const editor = useEditor({
    extensions: createContentBankExtensions({ openLinksOnClick: true }),
    content: value,
    editable: false,
  });

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.commands.setContent(latestValueRef.current, { emitUpdate: false });
    editor.setEditable(false);
  }, [contentKey, editor]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    applyHighlights(containerRef.current, searchQuery);
  }, [contentKey, editor, searchQuery]);

  return (
    <div ref={containerRef} className="content-bank-renderer">
      <EditorContent editor={editor} />
    </div>
  );
}
